// src/components/NavBar.js
import React from "react";
import {
    Avatar,
    Box,
    Button,
    Flex,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Spacer,
    useMediaQuery,
    MenuDivider,
    Text,
} from "@chakra-ui/react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {HamburgerIcon, ChevronDownIcon} from "@chakra-ui/icons";
import CampelLogo from "@assets/campel.svg?react";
import { useAuth } from "@context/AuthContext";
import { useIsAdmin } from "@shared/hooks/useIsAdmin";
import { useCanWriteYtg } from "@shared/hooks/useCanWriteYtg";
import type { User } from "@shared-types";

type IsActiveFn = (to: string) => boolean;
// Subset of the User type we actually consume in nav rendering. Tests
// stub `useAuth` with arbitrary objects so we keep it loose-ish.
type AuthUser = Pick<User, "name" | "email" | "picture"> & { name?: string | null };

interface DesktopModuleButtonsProps {
    isActive: IsActiveFn;
    canUseTools: boolean;
}

const DesktopModuleButtons = ({ isActive, canUseTools }: DesktopModuleButtonsProps): JSX.Element => (
    <>
        <Button
            as={Link}
            to="/app"
            size="md"
            bg="secondary.orange"
            color="white"
            _hover={{opacity: 0.9}}
            variant="solid"
            aria-current={isActive("/app") ? "page" : undefined}
            boxShadow={isActive("/app") ? "inset 0 0 0 2px white" : undefined}
        >
            Dashboard
        </Button>
        <Button
            as={Link}
            to="/ytg"
            size="md"
            bg="brand.ytg.main"
            color="white"
            _hover={{opacity: 0.9}}
            variant="solid"
            aria-current={isActive("/ytg") ? "page" : undefined}
            boxShadow={isActive("/ytg") ? "inset 0 0 0 2px white" : undefined}
        >
            YourText.Guru
        </Button>
        {canUseTools && <DesktopOutilsMenu isActive={isActive} />}
        {/* Clients is admin+tm only (read-only roles are 403'd server-side too). */}
        {canUseTools && (
            <Button
                as={Link}
                to="/clients"
                size="md"
                bg="secondary.green"
                color="white"
                _hover={{opacity: 0.9}}
                variant="solid"
                aria-current={isActive("/clients") ? "page" : undefined}
                boxShadow={isActive("/clients") ? "inset 0 0 0 2px white" : undefined}
            >
                Clients
            </Button>
        )}
    </>
);

const DesktopAdminMenu = (): JSX.Element => (
    <Menu>
        <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            size="md"
            bg="secondary.red"
            color="white"
            _hover={{opacity: 0.9}}
            _active={{opacity: 0.9}}
        >
            Admin
        </MenuButton>
        <MenuList>
            <MenuItem as={Link} to="/admin">
                Vue d'ensemble
            </MenuItem>
            <MenuDivider />
            <MenuItem as={Link} to="/admin/users">
                Utilisateurs
            </MenuItem>
            <MenuItem as={Link} to="/admin/ytg/operations">
                Opérations YTG
            </MenuItem>
            <MenuItem as={Link} to="/admin/stats">
                Statistiques
            </MenuItem>
        </MenuList>
    </Menu>
);

interface DesktopOutilsMenuProps {
    isActive: IsActiveFn;
}

const DesktopOutilsMenu = ({ isActive }: DesktopOutilsMenuProps): JSX.Element => (
    <Menu>
        <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            size="md"
            bg="secondary.purple"
            color="white"
            _hover={{opacity: 0.9}}
            _active={{opacity: 0.9}}
            boxShadow={isActive("/outils") ? "inset 0 0 0 2px white" : undefined}
        >
            Outils
        </MenuButton>
        <MenuList>
            <MenuItem as={Link} to="/outils">
                Vue d'ensemble
            </MenuItem>
            <MenuDivider />
            <MenuItem as={Link} to="/outils/extracteur">
                Extracteur de texte
            </MenuItem>
        </MenuList>
    </Menu>
);

interface DesktopAuthSectionProps {
    user: AuthUser | null;
    onLogout: () => Promise<void>;
}

const DesktopAuthSection = ({ user, onLogout }: DesktopAuthSectionProps): JSX.Element => {
    if (!user) {
        return (
            <Button
                as={Link}
                to="/login"
                size="md"
                bg="whiteAlpha.300"
                color="white"
                _hover={{bg: "whiteAlpha.400"}}
            >
                Se connecter
            </Button>
        );
    }
    return (
        <Menu>
            <MenuButton
                as={Button}
                leftIcon={
                    <Avatar
                        size="xs"
                        name={user.name || user.email}
                        src={user.picture ?? undefined}
                    />
                }
                rightIcon={<ChevronDownIcon />}
                size="md"
                bg="whiteAlpha.300"
                color="white"
                _hover={{bg: "whiteAlpha.400"}}
                _active={{bg: "whiteAlpha.400"}}
            >
                {/* Cap the label so an unusually long name/email can't blow out
                    the row — the desktop nav fits tightly at 992px. */}
                <Text as="span" isTruncated display="inline-block" verticalAlign="middle" maxW={{ lg: "150px", xl: "240px" }}>
                    {user.name || user.email}
                </Text>
            </MenuButton>
            <MenuList>
                <MenuItem as={Link} to="/mes-sessions">
                    Mes sessions
                </MenuItem>
                <MenuItem onClick={onLogout}>
                    Déconnexion
                </MenuItem>
            </MenuList>
        </Menu>
    );
};

interface DesktopNavProps {
    user: AuthUser | null;
    isAdmin: boolean;
    canUseTools: boolean;
    isActive: IsActiveFn;
    onLogout: () => Promise<void>;
}

const DesktopNav = ({ user, isAdmin, canUseTools, isActive, onLogout }: DesktopNavProps): JSX.Element => (
    <Flex gap={3} align="center">
        {user && <DesktopModuleButtons isActive={isActive} canUseTools={canUseTools} />}
        {isAdmin && <DesktopAdminMenu />}
        <DesktopAuthSection user={user} onLogout={onLogout} />
    </Flex>
);

interface MobileModuleItemsProps {
    isActive: IsActiveFn;
    canUseTools: boolean;
}

const MobileModuleItems = ({ isActive, canUseTools }: MobileModuleItemsProps): JSX.Element => (
    <>
        <MenuItem as={Link} to="/app" data-active={isActive("/app") ? "true" : undefined}>
            Dashboard
        </MenuItem>
        <MenuItem as={Link} to="/ytg" data-active={isActive("/ytg") ? "true" : undefined}>
            YourText.Guru
        </MenuItem>
        {canUseTools && (
            <MenuItem as={Link} to="/outils" data-active={isActive("/outils") ? "true" : undefined}>
                Outils
            </MenuItem>
        )}
        {canUseTools && (
            <MenuItem as={Link} to="/clients" data-active={isActive("/clients") ? "true" : undefined}>
                Clients
            </MenuItem>
        )}
    </>
);

interface MobileAdminItemsProps {
    isActive: IsActiveFn;
}

const MobileAdminItems = ({ isActive }: MobileAdminItemsProps): JSX.Element => (
    <>
        <MenuDivider />
        <MenuItem as={Link} to="/admin" data-active={isActive("/admin") ? "true" : undefined}>
            Admin
        </MenuItem>
    </>
);

interface MobileAuthItemsProps {
    user: AuthUser | null;
    onLogout: () => Promise<void>;
}

const MobileAuthItems = ({ user, onLogout }: MobileAuthItemsProps): JSX.Element => {
    if (!user) {
        return (
            <MenuItem as={Link} to="/login">
                Se connecter
            </MenuItem>
        );
    }
    return (
        <>
            <MenuItem isDisabled>
                <HStack spacing={2}>
                    <Avatar
                        size="xs"
                        name={user.name || user.email}
                        src={user.picture ?? undefined}
                    />
                    <Text fontSize="sm" color="gray.500">
                        {user.name || user.email}
                    </Text>
                </HStack>
            </MenuItem>
            <MenuItem as={Link} to="/mes-sessions">
                Mes sessions
            </MenuItem>
            <MenuItem onClick={onLogout}>
                Déconnexion
            </MenuItem>
        </>
    );
};

interface MobileNavProps {
    user: AuthUser | null;
    isAdmin: boolean;
    canUseTools: boolean;
    isActive: IsActiveFn;
    onLogout: () => Promise<void>;
}

const MobileNav = ({ user, isAdmin, canUseTools, isActive, onLogout }: MobileNavProps): JSX.Element => (
    <Menu>
        <MenuButton
            as={IconButton}
            icon={<HamburgerIcon/>}
            aria-label="Menu"
            color="white"
            bg="whiteAlpha.300"
            _hover={{bg: "whiteAlpha.400"}}
        />
        <MenuList>
            {user && <MobileModuleItems isActive={isActive} canUseTools={canUseTools} />}
            {isAdmin && <MobileAdminItems isActive={isActive} />}
            <MenuDivider />
            <MobileAuthItems user={user} onLogout={onLogout} />
        </MenuList>
    </Menu>
);

/**
 * Application top navigation bar.
 *
 * Renders the logo, module links, admin menu (for admins) and the auth
 * section, switching between a desktop and a mobile layout by viewport width.
 */
const NavBar = (): JSX.Element => {
    // Full desktop nav needs ~980px of content to fit next to the 150px logo,
    // so it overflowed between 769–991px. Switch to the hamburger up to `lg`
    // (991px) — tablets get the same menu as phones (it already holds everything).
    const [isMobile] = useMediaQuery("(max-width: 991px)");
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const isAdmin = useIsAdmin();
    const canUseTools = useCanWriteYtg();
    const isActive: IsActiveFn = (to) => pathname.startsWith(to);

    const handleLogout = async (): Promise<void> => {
        await logout();
        navigate('/login');
    };

    return (
        <Flex
            align="center"
            bg="principal.blue"
            w="100%"
            h="80px"
            px={4}
            position="sticky"
            top="0"
            zIndex="banner"
        >
            {/* Logo */}
            <Box py="10px">
                <Link to="/">
                    <Box color="principal.white" display="inline-block">
                        <CampelLogo style={{fill: "currentColor", width: 150, height: 40}}/>
                    </Box>
                </Link>
            </Box>

            <Spacer/>

            {isMobile ? (
                <MobileNav user={user} isAdmin={isAdmin} canUseTools={canUseTools} isActive={isActive} onLogout={handleLogout} />
            ) : (
                <DesktopNav user={user} isAdmin={isAdmin} canUseTools={canUseTools} isActive={isActive} onLogout={handleLogout} />
            )}
        </Flex>
    );
};

export default NavBar;
